import { Dispatch, SetStateAction } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { RecipeDialogStorageRamRow, RecipeRadio24 } from "./RecipeOptionBadges";
import type { ProcessedRecipe } from "./RecipesSectionDialog.types";

export type { ProcessedRecipe } from "./RecipesSectionDialog.types";

type RecipesSectionDialogProps = {
  isDialogOpen: boolean;
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
  currentRecipeKey?: string | null;
  onDialogRecipeRowClick: (recipeKey: string) => void;
  recipes: ProcessedRecipe[];
};

export const RecipesSectionDialog = ({
  isDialogOpen,
  setIsDialogOpen,
  currentRecipeKey,
  onDialogRecipeRowClick,
  recipes
}: RecipesSectionDialogProps) => {
  return (
    <div className="pt-4">
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full border-gray-dark-200 text-foreground hover:bg-gray-dark-600 dark:border-gray-light-1900"
          >
            Check More Recipes
          </Button>
        </DialogTrigger>
        <DialogContent
          showCloseIcon
          className={cn(
            "max-h-[90vh] max-w-[min(100vw-2rem,442px)] gap-0 overflow-hidden border-[1.25px] border-border p-0",
            "shadow-[-8px_8px_8px_0px_rgba(0,0,0,0.02)] sm:rounded-[8px]"
          )}
        >
          <DialogTitle className="sr-only">More Configurations</DialogTitle>

          {/* Header — Figma 1:5143 */}
          <div className="rounded-t-[8px] border-b border-border bg-background p-4">
            <p className="text-base font-medium leading-6 text-foreground">More Configurations</p>
          </div>

          {/* List — Figma 1:5145+ */}
          <div className="max-h-[min(60vh,520px)] overflow-y-auto bg-background">
            {recipes.map((recipe) => {
              const disabled = recipe.isSoldOut || !recipe.isCompatible;
              const isSelected = currentRecipeKey === recipe.key;

              return (
                <button
                  key={recipe.key}
                  type="button"
                  disabled={disabled}
                  className={cn(
                    "flex w-full flex-col gap-3 border-b border-border py-4 text-left last:border-b-0",
                    "transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    disabled && "cursor-not-allowed opacity-50"
                  )}
                  onClick={() => onDialogRecipeRowClick(recipe.key)}
                >
                  <div className="flex items-start justify-between gap-4 px-4">
                    <div className="min-w-0 flex flex-col gap-2 pr-2">
                      <p className="label-medium font-medium leading-5 text-foreground">{recipe.name}</p>
                      {!recipe.isCompatible ? (
                        <p className="subtext-x-small text-muted-foreground">
                          {recipe.availabilityLabel}
                        </p>
                      ) : recipe.isSoldOut ? (
                        <p className="subtext-x-small text-muted-foreground">Hardware Sold Out</p>
                      ) : (
                        <p className="subtext-small leading-4 text-muted-foreground">{recipe.description}</p>
                      )}
                    </div>
                    <RecipeRadio24 isSelected={isSelected} />
                  </div>

                  <div className="px-4">
                    <RecipeDialogStorageRamRow storage={recipe.storage} ram={recipe.ram} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer — Figma 1:5235 */}
          <div className="flex justify-end rounded-b-[8px] border-t border-border bg-background px-4 py-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-[8px] border-[1.25px] px-3 py-2 font-medium"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
